import React from 'react';
import { useIntl } from 'umi';
import { Modal, Button, notification } from 'tea-component';
import AceEditor from 'react-ace';
import Abnormal from '@home/components/Abnormal';
import { copyTextToClipboard } from '@/pages/datalab/utils/utils';
import './index.scss';

export interface IPreviewSQLModalProps {
  /** 是否显示弹窗 */
  visible: boolean;
  /** 关闭弹窗回调 */
  onClose: () => void;
  /** SQL 内容 */
  sql?: string;
  /** 请求加载中 */
  loading?: boolean;
  /** 请求失败 */
  error?: boolean;
}

/**
 * SQL 预览弹窗组件
 *
 * 负责展示格式化后的 SQL、加载态、失败态，并提供复制功能。
 */
export const PreviewSQLModal: React.FC<IPreviewSQLModalProps> = ({
  visible,
  onClose,
  sql = '',
  loading = false,
  error = false,
}) => {
  const { formatMessage } = useIntl();
  const canCopy = Boolean(sql) && !loading && !error;

  const handleCopy = (): void => {
    if (!canCopy) {
      return;
    }

    copyTextToClipboard(sql)?.then(
      () => {
        notification.success({
          description: formatMessage({ id: 'dbl_common_copy_success' }),
        });
      },
      err => {
        console.error('SQL 复制错误：', err);
        notification.error({
          description: formatMessage({ id: 'dbl_common_copy_error' }),
        });
      },
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      className="dbl-preview-sql-modal"
      maskClosable={true}
      caption={
        <>
          <span className="dbl-preview-sql-modal__caption-title">
            {formatMessage({ id: 'dbl_multi_preview_sql' })}
          </span>
          {canCopy ? (
            <Button
              type="link"
              className="dbl-preview-sql-modal__copy-btn"
              onClick={handleCopy}
            >
              {` ${formatMessage({ id: 'dbl_common_copy' })}`}
            </Button>
          ) : null}
        </>
      }
      size="l"
      onClose={onClose}
    >
      <Modal.Body>
        <Abnormal
          isLoading={loading}
          isError={error}
          failedMsg={formatMessage({ id: 'dbl_multi_preview_sql_fail' })}
          style={{ width: '100%', height: '700px' }}
          component={
            <AceEditor
              height="700px"
              aria-label="query editor input"
              mode="mysql"
              theme="tomorrow"
              fontSize={13}
              width="100%"
              showPrintMargin={false}
              showGutter
              highlightActiveLine={false}
              editorProps={{ $blockScrolling: true }}
              value={sql}
              readOnly={true}
            />
          }
        />
      </Modal.Body>
    </Modal>
  );
};

export default PreviewSQLModal;
